import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { ScreenHeader } from './poyais-ui/shell';
import { Button, Grid } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Notlar': 'Notes',
  'Pazarlama planlarını, notlarını ve dokümanlarını oluştur ve paylaş.': 'Create and share your marketing plans, notes, and documents.',
  'Yeni Not': 'New Note',
  'Tüm Notlar': 'All Notes',
  'Son': 'Recent',
  'Favoriler': 'Favorites',
  'Benim oluşturduklarım': 'Created by me',
  'Notlarda ara…': 'Search notes…',
  'Yükleniyor…': 'Loading…',
  'Henüz not yok.': 'No notes yet.',
  'Favori': 'Favorite',
  'Notu sil': 'Delete note',
  'Notu Düzenle': 'Edit Note',
  'BAŞLIK': 'TITLE',
  'İÇERİK': 'CONTENT',
  'Notu Sil': 'Delete Note',
  'Kapat': 'Close',
  'şimdi': 'now',
  'Yeni not': 'New note',
  'İçerik eklemek için tıkla…': 'Click to add content…',
});

const API = '/api/v1';

type Note = { id: number; title: string; body: string; author: string; avatar_bg?: string; is_favorite: number; updated_at: string };

const TABS = ['Tüm Notlar', 'Son', 'Favoriler', 'Benim oluşturduklarım'];

function relTime(iso: string, lang: 'tr' | 'en' = 'tr'): string {
  if (!iso) return '';
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (lang === 'en') {
    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.round(diffH / 24);
    if (diffD === 1) return 'yesterday';
    return `${diffD}d ago`;
  }
  if (diffMin < 1) return 'şimdi';
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH} sa önce`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'dün';
  return `${diffD} gün önce`;
}

export default function Notlar({ clientId = 1, userInitials = 'U' }: { clientId?: number; userInitials?: string }) {
  const { t, lang } = useLang();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TABS[0]);
  const [search, setSearch] = useState('');
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  const load = () => {
    axios.get(`${API}/notlar/?client_id=${clientId}`).then(r => setNotes(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId]);

  const addNote = () => {
    axios.post(`${API}/notlar/?client_id=${clientId}`, { title: 'Yeni not', body: 'İçerik eklemek için tıkla…', author: userInitials })
      .then(r => setNotes(ns => [r.data, ...ns])).catch(() => {});
  };

  const updateNote = (id: number, patch: Partial<Note>) => {
    setNotes(ns => ns.map(x => x.id === id ? { ...x, ...patch } : x));
    axios.patch(`${API}/notlar/${id}?client_id=${clientId}`, patch).catch(() => load());
  };

  const toggleFavorite = (n: Note) => updateNote(n.id, { is_favorite: n.is_favorite ? 0 : 1 } as Partial<Note>);

  const removeNote = (id: number) => {
    setNotes(ns => ns.filter(n => n.id !== id));
    axios.delete(`${API}/notlar/${id}?client_id=${clientId}`).catch(() => load());
    if (editingNote?.id === id) setEditingNote(null);
  };

  let visible = notes.filter(n => !search.trim() || n.title.toLowerCase().includes(search.trim().toLowerCase()));
  if (tab === 'Favoriler') visible = visible.filter(n => !!n.is_favorite);
  if (tab === 'Son') visible = visible.slice(0, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <ScreenHeader
        title={t('Notlar')}
        subtitle={t('Pazarlama planlarını, notlarını ve dokümanlarını oluştur ve paylaş.')}
        right={<Button variant="solid" onClick={addNote}>{t('Yeni Not')}</Button>}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 240, maxWidth: 520,
          border: `1px solid ${T.borderMid}`, borderRadius: 11, padding: '10px 13px', background: 'var(--py-surface)' }}>
          <Icon.search size={16} color="var(--py-text-mid-alt)" sw={2} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Notlarda ara…')}
            style={{ border: 'none', outline: 'none', fontSize: 13, color: T.text, flex: 1, fontFamily: 'inherit', background: 'transparent' }} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, borderBottom: `1px solid ${T.border}` }}>
        {TABS.map(tb => (
          <span key={tb} onClick={() => setTab(tb)} style={{ padding: '0 0 12px', fontSize: 13.5, fontWeight: tb === tab ? 700 : 600,
            color: tb === tab ? T.text : 'var(--py-text-mid-alt)', cursor: 'pointer',
            borderBottom: tb === tab ? `2px solid ${T.accent}` : 'none', marginBottom: tb === tab ? -1 : 0 }}>{t(tb)}</span>
        ))}
      </div>

      {loading ? (
        <PoyaisSpinner size={36} pad={24}/>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--py-text-mid-alt)', padding: '24px 2px' }}>{t('Henüz not yok.')}</div>
      ) : (
      <Grid min={270} gap={14}>
        {visible.map(n => (
          <div key={n.id} onClick={() => setEditingNote(n)}
            style={{ border: `1px solid ${T.border}`, borderRadius: 14, background: 'var(--py-surface)', padding: '18px 19px',
            cursor: 'pointer', minHeight: 150, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 4 }}>
              <button onClick={(e) => { e.stopPropagation(); toggleFavorite(n); }}
                style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent',
                  color: n.is_favorite ? T.accent : 'var(--py-border-soft)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                title={t('Favori')}>★</button>
              <button onClick={(e) => { e.stopPropagation(); removeNote(n.id); }}
                style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent',
                  color: 'var(--py-text-faint)', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
                title={t('Notu sil')}>✕</button>
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.015em', marginBottom: 8, paddingRight: 46 }}>{n.title}</div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--py-text-mid-alt)' }}>{n.body}</p>
            <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: n.avatar_bg || 'linear-gradient(135deg,#2A2D48,#4A4E6D)',
                color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n.author}</span>
              <span style={{ fontSize: 11.5, color: 'var(--py-text-mid-alt)' }}>{relTime(n.updated_at, lang)}</span>
            </div>
          </div>
        ))}
      </Grid>
      )}

      {editingNote && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,43,.45)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditingNote(null); }}>
          <div style={{ background: 'var(--py-surface)', borderRadius: 16, padding: '26px 28px', maxWidth: 520, width: '100%',
            maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(20,22,43,.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>{t('Notu Düzenle')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => toggleFavorite(editingNote)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, lineHeight: 1,
                    color: editingNote.is_favorite ? T.accent : 'var(--py-border-soft)' }} title={t('Favori')}>★</button>
                <button onClick={() => setEditingNote(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--py-text-dim)', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('BAŞLIK')}</div>
                <input value={editingNote.title}
                  onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                  onBlur={() => updateNote(editingNote.id, { title: editingNote.title })}
                  style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px',
                    fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('İÇERİK')}</div>
                <textarea value={editingNote.body}
                  onChange={(e) => setEditingNote({ ...editingNote, body: e.target.value })}
                  onBlur={() => updateNote(editingNote.id, { body: editingNote.body })}
                  rows={7}
                  style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px',
                    fontSize: 13, lineHeight: 1.55, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button onClick={() => removeNote(editingNote.id)}
                style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: 'var(--py-surface)',
                  color: 'var(--py-red-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('Notu Sil')}</button>
              <button onClick={() => setEditingNote(null)}
                style={{ flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--py-ink)',
                  color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('Kapat')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
